<template>
	<div class="progress-bar-renderer">
		<div class="progress-bar-renderer__labels">
			<span class="progress-bar-renderer__value">
				{{ valueLabel }}
				<span v-if="hasDifference" class="progress-bar-renderer__difference" :class="differenceClass">
					<v-icon v-if="differenceIcon">{{ differenceIcon }}</v-icon>
					{{ differenceLabel }}
				</span>
			</span>
			<span v-if="!params.hideObjective" class="progress-bar-renderer__objective">
				{{ objectiveText }}
				<span v-if="params.objectiveSuffix" class="progress-bar-renderer__objective-suffix" :class="params.objectiveSuffixClass">{{ params.objectiveSuffix }}</span>
			</span>
		</div>
		<v-progress-linear class="progress-bar-renderer__bar" :model-value="numericValue" :buffer-value="params.hideObjective ? progressMax : numericObjective" :max="progressMax" :color="barColor" :buffer-color="bufferColor" :buffer-opacity="bufferOpacity" :height="10" rounded />
	</div>
</template>

<script setup>
import { computed } from 'vue';

defineOptions({
	name: 'progressBarRenderer',
});

const DEFAULT_BUFFER_COLOR = 'grey-lighten-1';
const DEFAULT_BUFFER_OPACITY = 0.35;

const props = defineProps({
	params: {
		type: Object,
		required: true,
	},
});

const numericValue = computed(() => normalizeMetricValue(props.params?.value));
const numericObjective = computed(() => normalizeMetricValue(getObjectiveValue(props.params)));
const progressMax = computed(() => {
	if (props.params?.isPercentage) {
		return 100;
	}

	return Math.max(numericValue.value, numericObjective.value, 1);
});
const valueLabel = computed(() => formatMetricValue(props.params?.valueFormatted, props.params?.value));
const objectiveLabel = computed(() => formatMetricValue(null, getObjectiveValue(props.params)));
const objectiveText = computed(() => {
	if (props.params?.objectiveFormatted !== undefined) return props.params.objectiveFormatted;
	return props.params?.needLabel ? `${props.params?.objectiveLabel || 'Objective'}: ${objectiveLabel.value}` : objectiveLabel.value;
});
const barColor = computed(() => props.params?.barColor || props.params?.data?.color || props.params?.node?.aggData?.color || 'primary');
const bufferColor = computed(() => props.params?.bufferColor || DEFAULT_BUFFER_COLOR);
const bufferOpacity = computed(() => props.params?.bufferOpacity ?? DEFAULT_BUFFER_OPACITY);

// Touch-card mode passes differenceValue/differenceFormatted so the delta renders next to the value
// instead of its own line; ag-Grid cellRendererParams never sets these, so desktop is unaffected.
const numericDifference = computed(() => Number(props.params?.differenceValue));
const hasDifference = computed(() => props.params?.differenceFormatted !== null && props.params?.differenceFormatted !== undefined && props.params?.differenceFormatted !== '');
const differenceLabel = computed(() => props.params?.differenceFormatted ?? '');
const differenceClass = computed(() => {
	if (!Number.isFinite(numericDifference.value) || numericDifference.value === 0) return '';
	return numericDifference.value > 0 ? 'positive' : 'negative';
});
const differenceIcon = computed(() => {
	if (!Number.isFinite(numericDifference.value) || numericDifference.value === 0) return '';
	return numericDifference.value > 0 ? 'trending_up' : 'trending_down';
});

function getObjectiveValue(params) {
	if (params?.data?.objective !== undefined && params?.data?.objective !== null) {
		return params.data.objective;
	}

	if (params?.node?.aggData?.objective !== undefined && params?.node?.aggData?.objective !== null) {
		return params.node.aggData.objective;
	}

	return params?.objective ?? 0;
}

function formatMetricValue(preFormattedValue, rawValue) {
	if (preFormattedValue !== undefined && preFormattedValue !== null && preFormattedValue !== '') {
		return preFormattedValue;
	}

	if (typeof props.params?.formatMetric === 'function') {
		return props.params.formatMetric(rawValue);
	}

	const numericValue = Number(rawValue);
	if (!Number.isFinite(numericValue)) {
		return '';
	}

	const roundedValue = Math.round(numericValue * 100) / 100;
	return Number.isInteger(roundedValue) ? `${roundedValue}` : roundedValue.toFixed(2);
}

function normalizeMetricValue(value) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return 0;
	}

	return numericValue < 0 ? 0 : numericValue;
}
</script>

<style lang="scss">
@use '@/styles/variables' as *;

.progress-bar-renderer {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
	width: 100%;
	padding: 6px 0;
	gap: 4px;

	.progress-bar-renderer__labels {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;

		span {
			line-height: 1;
		}
	}

	.progress-bar-renderer__value {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		font-weight: 600;
		font-size: $font-size-large !important;
		color: rgb(var(--v-theme-on-surface));
	}

	.progress-bar-renderer__difference {
		display: inline-flex;
		align-items: center;
		gap: 2px;
		font-size: $font-size-small !important;
		font-weight: 600;

		.v-icon {
			font-size: 16px;
		}

		&.positive {
			color: $color-success;
		}

		&.negative {
			color: $color-error;
		}
	}

	.progress-bar-renderer__objective {
		font-size: $font-size-small;
		color: rgba(var(--v-theme-on-surface), 0.68);
		white-space: nowrap;
	}

	.progress-bar-renderer__objective-suffix {
		font-weight: 600;

		&.gauge--critical {
			color: $color-error;
		}

		&.gauge--low {
			color: $color-warning;
		}

		&.gauge--medium {
			color: #fbc02d;
		}

		&.gauge--high {
			color: $color-success;
		}
	}

	.progress-bar-renderer__bar {
		min-width: 0;
	}
}
</style>
